/**
 * Призрак комнаты-прямоугольника, пока её чертят инструментом стены (по диагонали или
 * повёрнутую): начерченный контур — внутренняя грань комнаты, стены — снаружи в свою толщину,
 * как они и встанут у отдельно стоящей комнаты. Внутри — длины сторон в чистоте и площадь.
 *
 * Цвет — как у черновика стены: перегородка синяя, несущая красная; комната задела бы
 * существующие стены — красный пунктир, ставить нельзя. Мышь сквозь призрак проходит.
 */
import { BEARING_HATCH } from "@/shared/catalog";
import { formatArea, formatMillimeters, insetPolygon, type Pt } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";

const PARTITION_COLOR = "#2f6feb";
const INVALID_COLOR = "#d64545";

interface Props {
	corners: Pt[];
	/** Длины первой и второй сторон, мм */
	width: number;
	depth: number;
	thickness: number;
	bearing: boolean;
	invalid: boolean;
	mmPerPx: number;
}

const pathOf = (points: Pt[]) => `M${points.map((p) => `${p.x} ${p.y}`).join("L")}Z`;

const midpoint = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export const RoomDraft = ({
	corners,
	width,
	depth,
	thickness,
	bearing,
	invalid,
	mmPerPx,
}: Props) => {
	const unit = useLengthUnit();
	const px = (n: number) => n * mmPerPx;
	const color = invalid ? INVALID_COLOR : bearing ? BEARING_HATCH.stroke : PARTITION_COLOR;
	// Отрицательный отступ — наружу: стены обступают начерченный контур
	const outer = insetPolygon(
		corners,
		corners.map(() => -thickness),
	);
	const center = midpoint(corners[0], corners[2]);
	/** Подпись стороны — у её середины, чуть внутрь комнаты */
	const sideLabel = (a: Pt, b: Pt) => {
		const mid = midpoint(a, b);
		const toCenter = { x: center.x - mid.x, y: center.y - mid.y };
		const length = Math.hypot(toCenter.x, toCenter.y) || 1;
		const shift = Math.min(px(16), length / 2);
		return { x: mid.x + (toCenter.x / length) * shift, y: mid.y + (toCenter.y / length) * shift };
	};
	const widthAt = sideLabel(corners[0], corners[1]);
	const depthAt = sideLabel(corners[1], corners[2]);
	const text = {
		textAnchor: "middle" as const,
		dominantBaseline: "middle" as const,
		fill: color,
		style: { userSelect: "none" as const },
	};
	return (
		<g style={{ pointerEvents: "none" }}>
			{/* Тела стен: между начерченным контуром и внешним */}
			{width > 0 && depth > 0 && (
				<path
					d={`${pathOf(outer)}${pathOf(corners)}`}
					fillRule="evenodd"
					fill={color}
					fillOpacity={0.22}
					stroke={color}
					strokeWidth={px(1.2)}
					strokeDasharray={invalid ? `${px(6)} ${px(4)}` : undefined}
				/>
			)}
			{width > 0 && (
				<text x={widthAt.x} y={widthAt.y} fontSize={px(12)} {...text}>
					{formatMillimeters(width, unit)}
				</text>
			)}
			{depth > 0 && (
				<text x={depthAt.x} y={depthAt.y} fontSize={px(12)} {...text}>
					{formatMillimeters(depth, unit)}
				</text>
			)}
			{width > 0 && depth > 0 && (
				<text x={center.x} y={center.y} fontSize={px(14)} fontWeight={600} {...text}>
					{formatArea(width * depth)}
				</text>
			)}
		</g>
	);
};
